import React, { useEffect, useState } from 'react';
import { X, Play, ImageOff } from 'lucide-react';
import { api, mediaUrl } from '../lib/api';
import { PageHero, Reveal, SectionTitle } from '../components/common';
import { img } from '../mock/mock';

const CATEGORIES = ['All', 'Garden', 'Gardens', 'Construction', 'Events', 'Wildlife', 'People'];

const Gallery = () => {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState('All');
  const [tab, setTab] = useState('all'); // all | image | video
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tab !== 'all') params.type = tab;
    if (filter !== 'All') params.category = filter;
    api.get('/media', { params })
      .then((res) => setMedia(res.data || []))
      .catch(() => setMedia([]))
      .finally(() => setLoading(false));
  }, [filter, tab]);

  const cats = ['All', ...Array.from(new Set(media.map((m) => m.category)))].filter((v, i, a) => a.indexOf(v) === i);
  const catList = cats.length > 1 ? cats : CATEGORIES.slice(0, 5);

  return (
    <div>
      <PageHero image={img.lushPath} kicker="Gallery" title="Moments from the garden" sub="A living archive of photos and films as the Roderick Gay Botanical Garden takes root." />

      <section className="py-20 bg-cream min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Explore" title="Photos & films" /></Reveal>

          {/* Type tabs */}
          <div className="flex justify-center gap-3 mt-8">
            {[['all', 'All'], ['image', 'Photos'], ['video', 'Videos']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-6 py-2.5 rounded-full text-sm font-600 transition-all ${tab === k ? 'bg-forest text-cream' : 'bg-white text-forest border border-sand hover:border-forest'}`}>{l}</button>
            ))}
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {catList.map((c) => (
              <button key={c} onClick={() => setFilter(c)} className={`px-4 py-1.5 rounded-full text-xs font-500 transition-all ${filter === c ? 'bg-gold text-white' : 'bg-white text-ink/70 border border-sand hover:border-gold'}`}>{c}</button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
              {Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-56 rounded-2xl bg-sand animate-pulse" />))}
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-24">
              <ImageOff className="w-12 h-12 text-forest/30 mx-auto" />
              <p className="mt-4 text-ink/50">No media yet. The garden team will be adding photos and films soon.</p>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 mt-12 [column-fill:_balance]">
              {media.map((m, i) => (
                <Reveal key={m.id} delay={(i % 4) * 60} className="mb-4 break-inside-avoid">
                  <button onClick={() => setActive(m)} className="group relative w-full rounded-2xl overflow-hidden shadow-md block">
                    {m.type === 'video' ? (
                      <div className="relative">
                        <video src={mediaUrl(m.url)} className="w-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 bg-forest-dark/30 flex items-center justify-center">
                          <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform"><Play className="w-6 h-6 text-forest ml-1" /></span>
                        </div>
                      </div>
                    ) : (
                      <img src={mediaUrl(m.url)} alt={m.title} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <div>
                        <p className="text-white font-display text-sm">{m.title}</p>
                        <p className="text-cream/70 text-xs">{m.category}</p>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-forest-dark/95 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <button className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" onClick={() => setActive(null)}><X className="w-6 h-6" /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {active.type === 'video' ? (
              <video src={mediaUrl(active.url)} className="w-full max-h-[80vh] rounded-xl" controls autoPlay />
            ) : (
              <img src={mediaUrl(active.url)} alt={active.title} className="w-full max-h-[80vh] object-contain rounded-xl" />
            )}
            <div className="text-center mt-4">
              <p className="text-white font-display text-lg">{active.title}</p>
              <p className="text-cream/60 text-sm">{active.category}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
